// src/components/layout/WorkbookReviewPrompt.jsx
import { useState, useEffect, useCallback } from "react";
import apiClient from "../../api/client";

export default function WorkbookReviewPrompt({
  workbookId,
  workbookTitle,
  onDone,
}) {
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [role, setRole] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Set when the submit came back 401. Keeps the prompt open.
  const [authError, setAuthError] = useState(false);
  const [featuredReviews, setFeaturedReviews] = useState([]);

  useEffect(() => {
    const isMounted = { current: true };
    apiClient
      .get(`/api/reviews/${workbookId}`)
      .then((data) => {
        if (!isMounted.current) return;
        const top2 = (data.reviews || [])
          .sort((a, b) => b.rating - a.rating)
          .slice(0, 2);
        setFeaturedReviews(top2);
      })
      .catch(() => {});
    return () => { isMounted.current = false; };
  }, [workbookId]);

  const handleSkip = useCallback(() => onDone(false), [onDone]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") handleSkip();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleSkip]);

  const handleSubmit = async () => {
    if (!rating) return;
    setSubmitting(true);
    setAuthError(false);
    try {
      await apiClient.post("/api/reviews", {
        workbookId,
        rating,
        reviewText: reviewText.trim() || null,
        role: role.trim() || null,
      });
      setSubmitted(true);
      setTimeout(() => onDone(true), 1500);
    } catch (err) {
      // A 401 is recoverable: hold the prompt open so the review text survives.
      if (err?.status === 401) setAuthError(true);
      else onDone();
    } finally {
      setSubmitting(false);
    }
  };

  const ratingLabels = ["", "Poor", "Fair", "Good", "Great", "Excellent! 🎉"];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleSkip}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,15,20,0.45)",
          backdropFilter: "blur(3px)",
          zIndex: 1000,
          animation: "rvFadeIn 180ms ease",
        }}
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          zIndex: 1001,
          width: "min(92vw, 460px)",
          background: "#F5F2EC",
          borderRadius: 16,
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
          padding: "2rem",
          animation: "rvSlideUp 220ms cubic-bezier(.22,.68,0,1.2)",
          fontFamily: "inherit",
        }}
      >
        {submitted ? (
          <div style={{ textAlign: "center", padding: "1.5rem 0" }}>
            <div style={{ fontSize: 40, marginBottom: ".75rem" }}>🌟</div>
            <p
              style={{
                fontFamily: "'Fraunces',serif",
                fontSize: 20,
                fontWeight: 700,
                color: "#1a1a1a",
                margin: "0 0 6px",
              }}
            >
              Thank you for your review!
            </p>
            <p style={{ fontSize: 13, color: "#71717a", margin: 0 }}>
              Your feedback helps other engineers decide.
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "1.5rem",
              }}
            >
              <div>
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#059669",
                    letterSpacing: ".08em",
                    textTransform: "uppercase",
                    margin: "0 0 4px",
                  }}
                >
                  ✓ Workbook complete
                </p>
                <h3
                  style={{
                    fontFamily: "'Fraunces',serif",
                    fontSize: 19,
                    fontWeight: 700,
                    color: "#1a1a1a",
                    margin: "0 0 4px",
                    lineHeight: 1.3,
                  }}
                >
                  Rate this workbook
                </h3>
                <p
                  style={{
                    fontSize: 12,
                    color: "#71717a",
                    margin: 0,
                    fontStyle: "italic",
                  }}
                >
                  "{workbookTitle}"
                </p>
              </div>
              <button
                onClick={handleSkip}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#a1a1aa",
                  fontSize: 20,
                  padding: "0 0 0 8px",
                }}
              >
                ×
              </button>
            </div>

            {/* Featured reviews */}
            {featuredReviews.length > 0 && (
              <>
                <div style={{ marginBottom: "1.25rem" }}>
                  {featuredReviews.map((r) => {
                    const name = r.userName || "";
                    const ini = r.initials || name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase() || "?";
                    const color = ["#2563eb","#059669","#D97706","#7c3aed","#dc2626"][(name.charCodeAt(0) || 0) % 5];
                    return (
                      <div key={r.id} style={{ display: "flex", gap: 10, marginBottom: "0.75rem" }}>
                        <div
                          style={{
                            width: 30, height: 30, borderRadius: "50%",
                            background: color, color: "white",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 11, fontWeight: 700, flexShrink: 0,
                          }}
                        >
                          {ini}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#1a1a1a" }}>{name}</span>
                            <span style={{ display: "flex", gap: 1 }}>
                              {[1, 2, 3, 4, 5].map((n) => (
                                <span key={n} style={{ color: "#D97706", fontSize: 11 }}>
                                  {n <= r.rating ? "★" : "☆"}
                                </span>
                              ))}
                            </span>
                          </div>
                          {r.reviewText && (
                            <p
                              style={{
                                fontFamily: "'Fraunces', serif",
                                fontSize: 13,
                                color: "#52525b",
                                fontStyle: "italic",
                                margin: 0,
                                lineHeight: 1.5,
                              }}
                            >
                              "{r.reviewText}"
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <hr style={{ border: "none", borderTop: "1px solid #e4e4e7", margin: "0 0 1rem" }} />
                <p style={{ fontSize: 13, fontWeight: 600, color: "#3f3f46", margin: "0 0 1.25rem" }}>
                  Your turn — how was it?
                </p>
              </>
            )}

            {/* Star rating */}
            <div style={{ marginBottom: "1.25rem" }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#3f3f46",
                  display: "block",
                  marginBottom: ".5rem",
                }}
              >
                Overall rating
              </label>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHovered(n)}
                    onMouseLeave={() => setHovered(0)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "2px 1px",
                      transition: "transform 100ms",
                      transform:
                        n <= (hovered || rating) ? "scale(1.2)" : "scale(1)",
                    }}
                  >
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill={n <= (hovered || rating) ? "#f59e0b" : "#d4d4d8"}
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  </button>
                ))}
                {(hovered || rating) > 0 && (
                  <span
                    style={{
                      fontSize: 13,
                      color: "#71717a",
                      marginLeft: 4,
                      fontWeight: 500,
                    }}
                  >
                    {ratingLabels[hovered || rating]}
                  </span>
                )}
              </div>
            </div>

            {/* Review text */}
            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#3f3f46",
                  display: "block",
                  marginBottom: ".5rem",
                }}
              >
                What did you get from this workbook?{" "}
                <span style={{ fontWeight: 400, color: "#a1a1aa" }}>
                  (optional)
                </span>
              </label>
              <textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                placeholder="e.g. Finally understood how CAD automation works. The analogies clicked immediately..."
                maxLength={500}
                rows={3}
                style={{
                  width: "100%",
                  border: "1.5px solid #e4e4e7",
                  borderRadius: 8,
                  padding: "8px 12px",
                  fontSize: 13,
                  color: "#1a1a1a",
                  background: "white",
                  resize: "vertical",
                  fontFamily: "inherit",
                  outline: "none",
                  boxSizing: "border-box",
                  lineHeight: 1.55,
                }}
              />
              <div
                style={{
                  textAlign: "right",
                  fontSize: 11,
                  color: "#a1a1aa",
                  marginTop: 2,
                }}
              >
                {reviewText.length}/500
              </div>
            </div>

            {/* Role */}
            <div style={{ marginBottom: "1.5rem" }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#3f3f46",
                  display: "block",
                  marginBottom: ".5rem",
                }}
              >
                Your role{" "}
                <span style={{ fontWeight: 400, color: "#a1a1aa" }}>
                  (optional — shown with review)
                </span>
              </label>
              <input
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="e.g. Mechanical Design Engineer"
                maxLength={100}
                style={{
                  width: "100%",
                  border: "1.5px solid #e4e4e7",
                  borderRadius: 8,
                  padding: "8px 12px",
                  fontSize: 13,
                  color: "#1a1a1a",
                  background: "white",
                  fontFamily: "inherit",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* A 401 keeps the prompt open with everything the user typed
                still in it. Closing on failure, which is what the silent
                onDone() below does for every other error, would throw the
                text away. */}
            {authError && (
              <div
                role="alert"
                style={{
                  background: "#FEF3C7",
                  border: "1px solid #FDE68A",
                  color: "#78350F",
                  borderRadius: 8,
                  padding: "9px 12px",
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  marginBottom: 14,
                }}
              >
                Your session expired. Sign in again, then resend this.
              </div>
            )}

            {/* Actions */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <button
                onClick={handleSkip}
                style={{
                  background: "none",
                  border: "none",
                  color: "#71717a",
                  fontSize: 13,
                  cursor: "pointer",
                  padding: "8px 0",
                  fontFamily: "inherit",
                }}
              >
                Skip
              </button>
              <button
                onClick={handleSubmit}
                disabled={!rating || submitting}
                style={{
                  background: !rating ? "#e4e4e7" : "#2563eb",
                  color: !rating ? "#a1a1aa" : "white",
                  border: "none",
                  borderRadius: 9,
                  padding: "9px 24px",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: !rating ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                  transition: "background 150ms",
                }}
              >
                {submitting ? "Submitting..." : "Submit review"}
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes rvFadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes rvSlideUp { from { opacity:0; transform:translate(-50%,calc(-50% + 16px)) } to { opacity:1; transform:translate(-50%,-50%) } }
      `}</style>
    </>
  );
}
